import BusinessIcon from "@mui/icons-material/Business";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { Box, MenuItem, TextField, Typography } from "@mui/material";
import React, { useEffect, useImperativeHandle, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  selectFiles,
  selectFormData,
  updateFileField,
  updateFormField,
  user,
} from "../../../ReduxStore/UserSlice";
import FileUploadField from "../../common/FileUpload/FileUploadField";
import { convertAndSetFile, getEntityTypeLabel } from "../../common/Util/Util";
import {
  entityDocUploadCompanyValidation,
  entityDocUploadProprietorshipValidation,
  entityDocUploadValidation,
  requirementBriefValidation,
} from "../../common/ValidationRules/validationRules";
const EntityDocUploadForm = React.forwardRef((props, ref) => {
  const { handleBack, disableRequirementBrief } = props;
  const {
    register,
    control,
    formState: { errors },
    setValue,
    trigger,
    watch,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      email: "",
      udyamRegistrationNo: "",
      fundRequirement: "",
      endUseOfFund: "",
      totalSalesCurrentYear: "",
      descriptionOfBusiness: "",
      lastFinancialYearTurnover: "",
      // currentFinancialYearTurnover: "",
      anticipatedGrowthRateCurrentYear: "",
      otherEndUseOfFund: "",
      companyBrief: null,
      estimatedCollateralValue: "",
    },
  });
  const dispatch = useDispatch();
  const userInfo = useSelector(user);
  const formData = useSelector(selectFormData);;
  const files = useSelector(selectFiles);
  const [uploadCompanyBrief, setUploadCompanyBrief] = useState(false);

  const watchedEndUseOfFund = watch("endUseOfFund");

  useImperativeHandle(ref, () => ({
    validateForm: async () => {
      if (!formData?.fundRequirement) {
        setValue("fundRequirement", "");
      }
      const result = await trigger();
      return result;
    },
  }));
  const getEmailFromSources = () => {
    if (userInfo?.Email || userInfo?.email) {
      return userInfo.Email || userInfo.email;
    }

    try {
      const localStorageUser = JSON.parse(localStorage.getItem("user"));
      if (localStorageUser?.Email || localStorageUser?.email) {
        return localStorageUser.Email || localStorageUser.email;
      }
    } catch (error) {
      toast.error("Error fetching email");
    }

    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    dispatch(updateFormField({ field: name, value: value }));
  };

  useEffect(() => {
    setValue("udyamRegistrationNo", formData.udyamRegistrationNo);

    if (!formData?.email) {
      const emailToUse = getEmailFromSources();
      if (emailToUse) {
        setValue("email", emailToUse);
        dispatch(updateFormField({ field: "email", value: emailToUse }));
      }
    } else {
      setValue("email", formData?.email);
    }

    setValue("fundRequirement", formData?.fundRequirement);
    setValue("endUseOfFund", formData?.endUseOfFund);
    setValue("totalSalesCurrentYear", formData?.totalSalesCurrentYear);
    setValue("descriptionOfBusiness", formData?.descriptionOfBusiness);
    setValue("lastFinancialYearTurnover", formData?.lastFinancialYearTurnover);
    // setValue(
    //   "currentFinancialYearTurnover",
    //   formData?.currentFinancialYearTurnover
    // );
    setValue(
      "anticipatedGrowthRateCurrentYear",
      formData?.anticipatedGrowthRateCurrentYear
    );
    setValue("companyBrief", convertAndSetFile(files.companyBrief));
    setValue("otherEndUseOfFund", formData?.otherEndUseOfFund);
    setValue("estimatedCollateralValue", formData?.estimatedCollateralValue ?? "");
  }, []);

  function numberToWords(num) {
    if (!num) return "Zero Rupees";

    const a = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const b = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    function inWords(n) {
      if (n === 0) return "";
      if (n < 20) return a[n];
      if (n < 100)
        return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
      if (n < 1000) {
        return (
          a[Math.floor(n / 100)] +
          " Hundred" +
          (n % 100 ? " and " + inWords(n % 100) : "")
        );
      }
      return "";
    }

    // Indian higher units
    const units = [
      { value: 1000000000000, name: "Kharab" }, // 10,00,00,00,00,000
      { value: 10000000000, name: "Arab" }, // 1,00,00,00,000
      { value: 10000000, name: "Crore" }, // 1,00,00,000
      { value: 100000, name: "Lakh" }, // 1,00,000
      { value: 1000, name: "Thousand" }, // 1,000
    ];

    let str = "";
    let remainder = num;

    for (let i = 0; i < units.length; i++) {
      const q = Math.floor(remainder / units[i].value);
      if (q) {
        str += inWords(q) + " " + units[i].name + " ";
        remainder = remainder % units[i].value;
      }
    }

    if (remainder > 0) {
      str += inWords(remainder) + " ";
    }

    return str.trim();
  }

  const getAmountInWords = (amount) => {
    if (!amount || isNaN(amount)) return "Zero Rupees";

    const loanAmount = Number(amount);
    return numberToWords(Math.floor(loanAmount)) + " Rupees";
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const handleFileSelect = (fieldName) => async (selectedFile) => {
    try {
      if (selectedFile) {
        const base64 = await fileToBase64(selectedFile);
        dispatch(
          updateFileField({
            field: fieldName,
            value: { base64, filename: selectedFile.name },
          })
        );
      }
    } catch (error) {
      console.error("Failed to convert file to base64:", error);
    }
  };
  return (
    <Box component="form">
      <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
        Step 2/5
      </Typography>

      <Box
        sx={{
          bgcolor: "#F8F9FA",
          p: 3,
          borderRadius: 1,
          mb: 4,
          backgroundColor: "#ECF4FF",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <DescriptionOutlinedIcon sx={{ mr: 1 }} />
          <Typography>
            You are submitting this application on behalf of{" "}
            <strong>{formData?.nameAsPerGST}</strong>
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Box
            sx={{
              backgroundColor: "white",
              p: 2,
              borderRadius: 1,
              width: "30%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <PersonOutlineIcon sx={{ mr: 1, color: "#666" }} />
              <Typography variant="caption">Name as per GST</Typography>
            </Box>
            <Typography variant="body1">{formData?.nameAsPerGST}</Typography>
          </Box>

          <Box
            sx={{
              backgroundColor: "white",
              p: 2,
              borderRadius: 1,
              width: "30%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <BusinessIcon sx={{ mr: 1, color: "#666" }} />
              <Typography variant="caption">Type of Entity</Typography>
            </Box>
            <Typography variant="body1">
              {getEntityTypeLabel(formData?.entityType)}
            </Typography>
          </Box>

          <Box
            sx={{
              backgroundColor: "white",
              p: 2,
              borderRadius: 1,
              width: "30%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <ReceiptIcon sx={{ mr: 1, color: "#666" }} />
              <Typography variant="caption">GST Number</Typography>
            </Box>
            <Typography variant="body1">{formData?.gstin}</Typography>
          </Box>
        </Box>
        <Typography
          component="div"
          sx={{
            display: "block",
            mt: 1,
            textAlign: "right",
          }}
        >
          Wrong Details?{" "}
          <Typography
            component="span"
            sx={{
              color: "#1976d2",
              cursor: "pointer",
            }}
            onClick={() => {
              handleBack();
            }}
          >
            Re-enter GST
          </Typography>
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Provide your business email for communication:
        </Typography>
        <TextField
          fullWidth
          label="email"
          required
          placeholder="abc@example.com"
          InputLabelProps={{ shrink: true }}
          sx={{ width: "75%" }}
          disabled={disableRequirementBrief}
          {...register("email", entityDocUploadValidation.email)}
          error={!!errors?.email}
          helperText={errors?.email?.message}
          value={formData?.email || ""}
          onChange={(e) => {
            register("email")?.onChange(e); // update RHF state
            handleChange(e); // update Redux or local state
          }}
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Enter your Udyam Registration number if applicable:
        </Typography>
        <TextField
          fullWidth
          label="Udyam Registration Number"
          placeholder="UDYAM-HR-00-123456"
          InputLabelProps={{ shrink: true }}
          sx={{ width: "75%" }}
          disabled={disableRequirementBrief}
          {...register(
            "udyamRegistrationNo",
            entityDocUploadValidation.udyamRegistrationNo
          )}
          error={!!errors.udyamRegistrationNo}
          helperText={errors.udyamRegistrationNo?.message}
          value={formData?.udyamRegistrationNo}
          onChange={(e) => {
            register("udyamRegistrationNo").onChange(e); // update RHF state
            handleChange(e); // update Redux or local state
          }}
        />
        <Typography
          variant="body2"
          sx={{ mt: 1, textAlign: "right", marginRight: "280px" }}
        >
          No Udyam number? Visit the{" "}
          <a
            href="https://udyamregistration.gov.in/Government-India/Ministry-MSME-registration.htm"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#1976d2",
              cursor: "pointer",
              textDecoration: "none",
            }}
          >
            Udyam registration site
          </a>
          .
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Desired Loan Amount (in INR)
          <span style={{ color: "red" }}>*</span>
        </Typography>

        <Controller
          name="fundRequirement"
          control={control}
          rules={requirementBriefValidation.fundRequirement}
          render={({ field }) => (
            <NumericFormat
              {...field}
              customInput={TextField}
              fullWidth
              placeholder="Enter fund requirement"
              thousandSeparator=","
              thousandsGroupStyle="lakh"
              allowNegative={false}
              decimalScale={0} // integers only
              inputProps={{ maxLength: 16 }}
              disabled={disableRequirementBrief}
              error={!!errors?.fundRequirement}
              helperText={
                errors.fundRequirement?.message
                  ? errors.fundRequirement?.message
                  : getAmountInWords(formData?.fundRequirement)
              }
              value={formData?.fundRequirement}
              onValueChange={(values) => {
                const { value } = values;
                field.onChange(value);
                handleChange({ target: { name: "fundRequirement", value } });
              }}
            />
          )}
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          End use of Funds (e.g. working capital or business expansion, others)
          <span style={{ color: "red" }}>*</span>
        </Typography>

        <Controller
          name="endUseOfFund"
          control={control}
          rules={requirementBriefValidation?.endUseOfFund}
          render={({ field }) => {
            const isOther = field.value === "Other";
            return (
              <>
                {/* Dropdown */}
                <TextField
                  select
                  {...field}
                  fullWidth
                  label="Select End Use"
                  disabled={disableRequirementBrief}
                  error={!!errors?.endUseOfFund}
                  helperText={errors?.endUseOfFund?.message}
                  onChange={(e) => {
                    field.onChange(
                      e.target.value === "Other" ? "Other" : e.target.value
                    );
                    handleChange(e);
                  }}
                >
                  <MenuItem value="working_capital">Working Capital</MenuItem>
                  <MenuItem value="business_expansion">
                    Business Expansion
                  </MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>

                {/* Conditional TextField when "Other" selected */}
                {isOther && (
                  <Controller
                    name="otherEndUseOfFund"
                    control={control}
                    rules={{
                      required: watchedEndUseOfFund === "other" ? "Please specify the end use of fund" : false,
                    }}
                    render={({ field: otherField }) => (
                      <TextField
                        {...otherField}
                        fullWidth
                        sx={{ mt: 2 }}
                        placeholder="Please specify"
                        disabled={disableRequirementBrief}
                        value={formData?.otherEndUseOfFund}
                        onChange={(e) => {
                          otherField.onChange(e);
                          handleChange(e);
                          // Update endUseOfFund to the entered value
                          dispatch(updateFormField({ field: "endUseOfFund", value: e.target.value }));
                        }}
                        error={!!errors?.otherEndUseOfFund}
                        helperText={errors?.otherEndUseOfFund?.message}
                      />
                    )}
                  />
                )}
              </>
            );
          }}
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Year Till Date Sales (in INR)
          <span style={{ color: "red" }}>*</span>
        </Typography>
        <Controller
          name="totalSalesCurrentYear"
          control={control}
          rules={entityDocUploadCompanyValidation.totalSalesCurrentYear}
          render={({ field }) => (
            <NumericFormat
              {...field}
              customInput={TextField}
              fullWidth
              placeholder="Enter amount"
              sx={{ width: "75%" }}
              thousandSeparator=","
              thousandsGroupStyle="lakh"
              allowNegative={false}
              decimalScale={0}
              inputProps={{ maxLength: 16 }}
              disabled={disableRequirementBrief}
              error={!!errors.totalSalesCurrentYear}
              helperText={
                errors.totalSalesCurrentYear?.message
                  ? errors.totalSalesCurrentYear?.message
                  : getAmountInWords(formData?.totalSalesCurrentYear)
              }
              value={formData?.totalSalesCurrentYear ?? ""}
              onChange={() => {}}
              onValueChange={(values) => {
                const { value } = values;
                field.onChange(value);
                handleChange({ target: { name: "totalSalesCurrentYear", value } });
              }}
            />
          )}
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Company Brief
          <span style={{ color: "red" }}>*</span>
        </Typography>
        <Controller
          name="descriptionOfBusiness"
          control={control}
          rules={requirementBriefValidation?.descriptionOfBusiness}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              multiline
              rows={4}
              placeholder="Describe your core operations, products/services, and target customers."
              disabled={disableRequirementBrief}
              error={!!errors?.descriptionOfBusiness}
              helperText={errors?.descriptionOfBusiness?.message}
              value={formData?.descriptionOfBusiness || ""}
              onChange={(e) => {
                field.onChange(e);
                handleChange(e);
              }}
            />
          )}
        />

        {/* Upload link */}
        <Typography
          variant="body2"
          sx={{
            mt: 1,
            textAlign: "right",
            color: "primary.main",
            textDecoration: "underline",
            cursor: "pointer",
          }}
          onClick={() => {
            if (disableRequirementBrief) return;
            if (uploadCompanyBrief) {
              setUploadCompanyBrief(false);
            } else {
              setUploadCompanyBrief(true);
            }
          }}
        >
          {uploadCompanyBrief || files.companyBrief
            ? "Remove Documnet"
            : "Upload document"}
        </Typography>

        {(uploadCompanyBrief || files?.companyBrief) && (
          <Controller
            name="companyBrief"
            control={control}
            rules={entityDocUploadProprietorshipValidation.companyBrief}
            render={({ field }) => (
              <FileUploadField
                name="companyBrief"
                label={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    Company Brief Document
                  </Box>
                }
                error={!!errors.companyBrief}
                helperText={errors.companyBrief?.message}
                value={convertAndSetFile(files?.companyBrief)}
                onFileSelect={(file) => {
                  if (disableRequirementBrief) return;
                  field.onChange(file);
                  handleFileSelect("companyBrief")(file);
                }}
              />
            )}
          />
        )}
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Last FY Sales Turnover (in INR)
        </Typography>
        <Controller
          name="lastFinancialYearTurnover"
          control={control}
          render={({ field }) => (
            <NumericFormat
              {...field}
              customInput={TextField}
              fullWidth
              placeholder="Enter amount"
              sx={{ width: "75%", mb: 2 }}
              thousandSeparator=","
              thousandsGroupStyle="lakh"
              allowNegative={false}
              decimalScale={0}
              inputProps={{ maxLength: 16 }}
              disabled={disableRequirementBrief}
              error={!!errors.lastFinancialYearTurnover}
              helperText={
                errors.lastFinancialYearTurnover?.message
                  ? errors.lastFinancialYearTurnover?.message
                  : getAmountInWords(formData?.lastFinancialYearTurnover)
              }
              value={formData?.lastFinancialYearTurnover ?? ""}
              onChange={() => {}}
              onValueChange={(values) => {
                const { value } = values;
                field.onChange(value);
                handleChange({ target: { name: "lastFinancialYearTurnover", value } });
              }}
            />
          )}
        />

        <Typography variant="body2" sx={{ mb: 1 }}>
          Projected Turnover for current FY (in INR)
        </Typography>
        <Controller
          name="anticipatedGrowthRateCurrentYear"
          control={control}
          render={({ field }) => (
            <NumericFormat
              {...field}
              customInput={TextField}
              fullWidth
              placeholder="Enter amount"
              sx={{ width: "75%" }}
              thousandSeparator=","
              thousandsGroupStyle="lakh"
              allowNegative={false}
              decimalScale={0}
              inputProps={{ maxLength: 16 }}
              disabled={disableRequirementBrief}
              error={!!errors.anticipatedGrowthRateCurrentYear}
              helperText={
                errors.anticipatedGrowthRateCurrentYear?.message
                  ? errors.anticipatedGrowthRateCurrentYear?.message
                  : getAmountInWords(formData?.anticipatedGrowthRateCurrentYear)
              }
              value={formData?.anticipatedGrowthRateCurrentYear ?? ""}
              onChange={() => {}}
              onValueChange={(values) => {
                const { value } = values;
                field.onChange(value);
                handleChange({ target: { name: "anticipatedGrowthRateCurrentYear", value } });
              }}
            />
          )}
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Estimated Collateral (in INR)
        </Typography>
        <Controller
          name="estimatedCollateralValue"
          control={control}
          render={({ field }) => (
            <NumericFormat
              {...field}
              customInput={TextField}
              fullWidth
              placeholder="Enter estimated collateral value"
              sx={{ width: "75%" }}
              thousandSeparator=","
              thousandsGroupStyle="lakh"
              allowNegative={false}
              decimalScale={0}
              inputProps={{ maxLength: 16 }}
              disabled={disableRequirementBrief}
              error={!!errors.estimatedCollateralValue}
              helperText={
                errors.estimatedCollateralValue?.message
                  ? errors.estimatedCollateralValue?.message
                  : getAmountInWords(formData?.estimatedCollateralValue)
              }
              value={formData?.estimatedCollateralValue ?? ""}
              onChange={() => {}}
              onValueChange={(values) => {
                const { value } = values ?? {};
                field.onChange(value);
                handleChange({ target: { name: "estimatedCollateralValue", value: value ?? "" } });
              }}
            />
          )}
        />
      </Box>
    </Box>
  );
});

export default EntityDocUploadForm;
